import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Chart } from 'chart.js';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	DiscoveryProfileComponent,
	DiscoveryProfileOverviewData,
	DiscoveryProfilePersonalAnalysisData,
	SocialPlatform
} from 'components';
import { DiscoveryProfileHttpService } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/discovery-profile.http.service';
import { NavProfilePlatform } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/models/discovery-profile.model';
import { mapApiToDiscoveryProfileData } from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/discovery-profile-api.mapper';
import {
	mapApiToPersonalAnalysis,
	mapPersonalAnalysisToProfileData
} from '@brand-lolly/app/social-media-influencer/modules/discovery-v2/components/discovery-profile/services/discovery-personal-analysis-api.mapper';

// Hard cap so Gotenberg never hangs if an asset stalls.
const EXPORT_TIMEOUT_MS = 45000;
// Small paint delay after a tab switch before snapshotting. chart.js animation
// is disabled on this page (see ngOnInit), so a redraw is near-instant — we
// just let Angular re-render and the browser paint one frame.
const CHART_SETTLE_MS = 150;
// CONTRACT: the profile's in-card switches are lo-segmented-tabs (one role=tab button
// per option); app-tab-selector stays matched for any card not yet moved.
const TAB_STRIP = 'lo-segmented-tabs, app-tab-selector';
const TAB_OPTION = '[role="tab"], .option';
// Even shorter for list/number cards, which update synchronously.
const LIST_SETTLE_MS = 40;

@Component({
	standalone: true,
	selector: 'app-discovery-profile-export',
	templateUrl: './discovery-profile-export.component.html',
	styleUrls: ['./discovery-profile-export.component.scss'],
	imports: [CommonModule, DiscoveryProfileComponent]
})
export class DiscoveryProfileExportComponent implements OnInit, OnDestroy {
	overviewData: DiscoveryProfileOverviewData | null = null;
	personalAnalysisData: DiscoveryProfilePersonalAnalysisData | null = null;
	errorMessage = '';
	// Bound to app-discovery-profile; the print page has no session/brand context.
	readonly socialMediaControl = new FormControl<SocialPlatform>('instagram', {
		nonNullable: true
	});

	private readonly destroy$ = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private httpService: DiscoveryProfileHttpService,
		private cdRef: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		(window as unknown as Record<string, unknown>)[
			'discoveryProfileExportReady'
		] = false;
		document.body.classList.add('discovery-profile-export-mode');
		// Disable chart.js animation for the whole print page so charts draw
		// instantly — the tab-duplication step redraws each chart per tab
		// combination and must not wait ~1s for an animation each time.
		(Chart.defaults as { animation: unknown }).animation = false;

		setTimeout(() => this.markReady('timeout'), EXPORT_TIMEOUT_MS);

		const discoveryId = Number(
			this.route.snapshot.paramMap.get('discoveryId')
		);
		const platform = (this.route.snapshot.paramMap.get('platform') ??
			'') as NavProfilePlatform;
		const exportToken =
			this.route.snapshot.queryParamMap.get('exportToken') ?? '';

		if (!discoveryId || !platform || !exportToken) {
			this.markError('Missing export parameters.');
			return;
		}
		this.socialMediaControl.setValue(platform as SocialPlatform, {
			emitEvent: false
		});

		this.httpService
			.getReportForExport(discoveryId, platform, exportToken)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: result => {
					this.overviewData = mapApiToDiscoveryProfileData(
						result.data
					);
					this.personalAnalysisData = result.personal_analysis
						? mapPersonalAnalysisToProfileData(
								mapApiToPersonalAnalysis(
									result.personal_analysis
								)
						  )
						: null;
					this.cdRef.detectChanges();
					this.expandAllSections();
					this.cdRef.detectChanges();
					void this.finalizeForPrint();
				},
				error: () => this.markError('Failed to load report data.')
			});
	}

	ngOnDestroy(): void {
		document.body.classList.remove('discovery-profile-export-mode');
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Open every collapsible section so the PDF shows all data. Each toggle
	 * ("Load More" in brand-affinity / brand-partnerships / mentions, and
	 * "Show Detailed Breakdown" in the ROI forecast) starts collapsed on a
	 * fresh render, so a single click expands it. The toggles themselves are
	 * hidden via CSS in the export stylesheet.
	 */
	private expandAllSections(): void {
		document
			.querySelectorAll<HTMLElement>('.load-more, .breakdown-link')
			.forEach(toggle => toggle.click());
	}

	/** Expand tabbed cards, wait for everything to settle, then mark ready. */
	private async finalizeForPrint(): Promise<void> {
		// Initial render + expand animations.
		await this.wait(400);
		await this.duplicateTabbedCards();
		this.cdRef.detectChanges();
		await Promise.all([this.waitForFonts(), this.waitForImages()]);
		await this.wait(500);
		this.markReady('all-ready');
	}

	/**
	 * Print every tab of a tabbed card, not just the active one: for each card
	 * that contains a tab selector, click through the selector's options and
	 * take a static snapshot per option, then replace the live card with the
	 * stack of snapshots. Canvas charts (chart.js) are rasterised to <img> so
	 * the cloned copies aren't blank.
	 */
	private async duplicateTabbedCards(): Promise<void> {
		const cards = new Set<HTMLElement>();
		document.querySelectorAll<HTMLElement>(TAB_STRIP).forEach(selector => {
			const card = selector.closest<HTMLElement>('app-dp-section-card');
			if (card) cards.add(card);
		});

		for (const card of cards) {
			await this.duplicateCard(card);
		}
	}

	private async duplicateCard(card: HTMLElement): Promise<void> {
		// A card can carry more than one toggle (e.g. Growth Trend has a
		// Followers/Likes AND a 30d/15d/7d selector; Age & Gender has a
		// Followers/Likers AND an All/Male/Female selector). Print every
		// COMBINATION of their options, not just the primary toggle.
		const multiOptionSelectors = (): HTMLElement[] =>
			Array.from(card.querySelectorAll<HTMLElement>(TAB_STRIP)).filter(
				selector => selector.querySelectorAll(TAB_OPTION).length >= 2
			);

		const sizes = multiOptionSelectors().map(
			selector => selector.querySelectorAll(TAB_OPTION).length
		);
		if (!sizes.length) return;

		const combinations = this.cartesianIndices(sizes);
		if (combinations.length < 2) return;

		const settle = card.querySelector('canvas')
			? CHART_SETTLE_MS
			: LIST_SETTLE_MS;

		const snapshots: HTMLElement[] = [];
		for (const combination of combinations) {
			// Apply each selector's choice IN ORDER, re-querying fresh each
			// click: switching one toggle can rebuild a later toggle's option
			// nodes (Age & Gender rebuilds the age filter when the segment
			// changes), so cached node references would go stale.
			combination.forEach((optionIndex, setIndex) => {
				const options = Array.from(
					multiOptionSelectors()[
						setIndex
					]?.querySelectorAll<HTMLElement>(TAB_OPTION) ?? []
				);
				options[optionIndex]?.click();
				this.cdRef.detectChanges();
			});
			await this.wait(settle);
			snapshots.push(this.snapshotCard(card));
		}

		const parent = card.parentNode;
		if (!parent) return;
		snapshots.forEach(snapshot => parent.insertBefore(snapshot, card));
		parent.removeChild(card);
	}

	/** Cartesian product of option indices for the given per-selector counts. */
	private cartesianIndices(sizes: number[]): number[][] {
		return sizes.reduce<number[][]>(
			(accumulator, size) => {
				const expanded: number[][] = [];
				accumulator.forEach(prefix => {
					for (let index = 0; index < size; index++) {
						expanded.push([...prefix, index]);
					}
				});
				return expanded;
			},
			[[]]
		);
	}

	/**
	 * Deep-clone a card, replacing every live chart.js canvas with a static
	 * PNG of its current pixels (a bare cloned <canvas> would be blank).
	 */
	private snapshotCard(card: HTMLElement): HTMLElement {
		const clone = card.cloneNode(true) as HTMLElement;
		const liveCanvases = card.querySelectorAll<HTMLCanvasElement>('canvas');
		const cloneCanvases =
			clone.querySelectorAll<HTMLCanvasElement>('canvas');
		liveCanvases.forEach((live, index) => {
			const target = cloneCanvases[index];
			if (!target) return;
			let dataUrl: string;
			try {
				dataUrl = live.toDataURL('image/png');
			} catch {
				return;
			}
			const image = document.createElement('img');
			image.src = dataUrl;
			image.style.width = `${live.offsetWidth}px`;
			image.style.height = `${live.offsetHeight}px`;
			target.replaceWith(image);
		});
		return clone;
	}

	private waitForFonts(): Promise<void> {
		if (!document.fonts?.ready) return Promise.resolve();
		return Promise.race([
			document.fonts.ready.then(() => undefined),
			this.wait(4000)
		]);
	}

	private waitForImages(): Promise<void> {
		const pending = Array.from(
			document.querySelectorAll<HTMLImageElement>('img')
		).filter(img => !img.complete);
		if (!pending.length) return Promise.resolve();
		return Promise.race([
			Promise.all(
				pending.map(
					img =>
						new Promise<void>(resolve => {
							img.addEventListener('load', () => resolve(), {
								once: true
							});
							img.addEventListener('error', () => resolve(), {
								once: true
							});
						})
				)
			).then(() => undefined),
			this.wait(4000)
		]);
	}

	private wait(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	private markError(message: string): void {
		this.errorMessage = message;
		this.markReady('error');
		this.cdRef.detectChanges();
	}

	private markReady(reason: string): void {
		const win = window as unknown as Record<string, unknown>;
		if (win['discoveryProfileExportReady'] === true) return;
		win['discoveryProfileExportReady'] = true;
		// eslint-disable-next-line no-console
		console.log('[discovery-profile-export] ready reason=%s', reason);
	}
}
